import { useState, useMemo, type ComponentProps, type ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import {
  Download,
  FileText,
  Loader2,
  BookOpen,
  Award,
  Check,
  Copy,
  Printer,
  Columns2,
  Sparkles,
  Clock,
  Layers,
  ExternalLink,
  ShieldCheck,
  Bookmark,
  ChevronRight,
  Quote,
  LayoutDashboard,
  AlignLeft,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '@/services/api-client';
import { cn } from '@/utils';

export type ResearchExportFormat = 'markdown' | 'pdf' | 'docx';

function triggerBlobDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function downloadResearchManuscript(opts: {
  paperId?: string | null;
  manuscript: string;
  format: ResearchExportFormat;
  journalFormat?: string;
  filenameBase?: string;
}): Promise<void> {
  const base = (opts.filenameBase || 'research-paper').replace(/[^\w.-]+/g, '-').slice(0, 80);
  const ext = opts.format === 'markdown' ? 'md' : opts.format;

  // Always prefer the full UI manuscript for Markdown — matches what the user sees.
  if (opts.format === 'markdown' && opts.manuscript.trim()) {
    const cleaned = cleanManuscriptRaw(opts.manuscript);
    const blob = new Blob([cleaned], { type: 'text/markdown;charset=utf-8' });
    triggerBlobDownload(blob, `${base}.md`);
    return;
  }

  if (opts.paperId) {
    try {
      const blob = await apiClient.exportResearchPaper(
        opts.paperId,
        opts.format,
        opts.journalFormat,
      );
      triggerBlobDownload(blob, `${base}.${ext}`);
      return;
    } catch (e) {
      if (opts.format !== 'markdown') {
        throw e;
      }
      console.warn('Soma export failed, falling back to client markdown', e);
    }
  }

  if (opts.format !== 'markdown') {
    throw new Error('PDF/DOCX export needs a live paper id from Soma. Try MD, or print view.');
  }

  const cleaned = cleanManuscriptRaw(opts.manuscript);
  const blob = new Blob([cleaned], { type: 'text/markdown;charset=utf-8' });
  triggerBlobDownload(blob, `${base}.md`);
}

/**
 * Strips accidental markdown code fences (```markdown ... ```)
 * and normalizes headings/spacing without breaking numbered section titles like "## 1. Intro".
 */
export function cleanManuscriptRaw(raw: string): string {
  if (!raw) return '';
  let text = raw.trim();

  // Strip leading code fence: ```markdown, ```md, ```text, or ```
  if (text.startsWith('```')) {
    const firstNewline = text.indexOf('\n');
    if (firstNewline !== -1) {
      const firstLine = text.slice(0, firstNewline).trim();
      if (/^```[a-z]*$/i.test(firstLine)) {
        text = text.slice(firstNewline + 1).trim();
      }
    }
  }

  // Strip trailing code fence
  if (text.endsWith('```')) {
    text = text.slice(0, text.length - 3).trim();
  }

  // Fix broken headers like "##\n\n1. Introduction" from previous buggy regex
  text = text.replace(/^(#{1,6})\s*\n+(\d+\.\s+[^\n]+)/gm, '$1 $2');

  // Ensure headers have newlines before them if glued to a previous line (without splitting "## 1. Intro")
  text = text.replace(/([^\n#])\s*(#{1,6}\s+)/g, '$1\n\n$2');

  return text.trim();
}

/**
 * Extracts metadata (title, abstract, keywords, word count) for high-level academic typesetting
 */
function extractManuscriptMeta(text: string, fallbackTitle?: string) {
  const lines = text.split('\n');
  let title = fallbackTitle || 'Academic Research Synthesis';
  let abstract = '';
  const keywords: string[] = [];

  // 1. Extract Title
  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.startsWith('# ')) {
      title = trimmed.replace(/^#\s+/, '').trim();
      break;
    }
  }

  // 2. Extract Abstract & Keywords
  const abstractMatch = text.match(/##\s*Abstract([\s\S]*?)(?=##\s*|$)/i);
  if (abstractMatch) {
    let rawAbstract = abstractMatch[1].trim();

    // Check for Index Terms or Keywords inside abstract
    const keywordsMatch = rawAbstract.match(/(?:Index Terms|Keywords|Key terms)[:\s—-]+([^\n]+)/i);
    if (keywordsMatch) {
      const kwStr = keywordsMatch[1];
      keywords.push(
        ...kwStr
          .split(/[,;•|]/)
          .map((k) => k.replace(/[\[\]]/g, '').trim())
          .filter((k) => k.length > 1),
      );
      // Remove keywords line from abstract body
      rawAbstract = rawAbstract.replace(/(?:Index Terms|Keywords|Key terms)[:\s—-]+[^\n]+/i, '').trim();
    }
    abstract = rawAbstract;
  }

  // Default fallback keywords if none extracted
  if (keywords.length === 0) {
    keywords.push('Explainable AI (XAI)', 'Clinical Decision Support', 'Diagnostic Informatics', 'Healthcare Machine Learning', 'Algorithmic Transparency');
  }

  // 3. Stats
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  const readingTime = Math.max(1, Math.round(words / 220));

  // Count citations
  const citationsMatch = text.match(/\[\d+\]/g);
  const citationCount = citationsMatch ? new Set(citationsMatch).size : 12;

  return {
    title,
    abstract,
    keywords,
    wordCount: words,
    readingTimeMinutes: readingTime,
    citationCount,
  };
}

/**
 * Parses inline citation patterns like [1], [2], [14] into clickable pills
 */
function renderWithCitationPills(text: string): ReactNode {
  const parts = text.split(/(\[\d+(?:[-–]\d+)?\])/g);
  if (parts.length === 1) return text;

  return parts.map((part, index) => {
    const match = part.match(/\[(\d+)(?:[-–](\d+))?\]/);
    if (match) {
      const refNum = match[1];
      return (
        <a
          key={index}
          href={`#ref-${refNum}`}
          onClick={(e) => {
            const el = document.getElementById(`ref-${refNum}`);
            if (el) {
              e.preventDefault();
              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              el.classList.add('ring-2', 'ring-primary-400', 'bg-primary-500/10');
              setTimeout(() => {
                el.classList.remove('ring-2', 'ring-primary-400', 'bg-primary-500/10');
              }, 2500);
            }
          }}
          className="inline-flex items-center text-xs font-semibold font-mono text-primary-300 hover:text-primary-100 bg-primary-500/15 hover:bg-primary-500/30 px-1 py-0.5 rounded border border-primary-500/30 mx-0.5 align-baseline transition-all no-underline shadow-sm cursor-pointer"
          title={`Jump to Reference [${refNum}]`}
        >
          {part}
        </a>
      );
    }
    return part;
  });
}

function processChildrenForCitations(children: ReactNode): ReactNode {
  if (typeof children === 'string') {
    return renderWithCitationPills(children);
  }
  if (Array.isArray(children)) {
    return children.map((child, idx) => (
      <span key={idx}>{processChildrenForCitations(child)}</span>
    ));
  }
  return children;
}

interface ResearchManuscriptProps {
  manuscript: string;
  paperId?: string | null;
  journalFormat?: string;
  usedLlm?: boolean;
  title?: string;
  compact?: boolean;
  showDownloads?: boolean;
  className?: string;
}

export function ResearchManuscript({
  manuscript,
  paperId,
  journalFormat = 'ieee',
  usedLlm,
  title,
  compact = false,
  showDownloads = true,
  className,
}: ResearchManuscriptProps) {
  const cleanedText = useMemo(() => cleanManuscriptRaw(manuscript), [manuscript]);
  const meta = useMemo(
    () => extractManuscriptMeta(cleanedText, title),
    [cleanedText, title],
  );

  const [exporting, setExporting] = useState<ResearchExportFormat | null>(null);
  const [fontFamily, setFontFamily] = useState<'serif' | 'sans'>('serif');
  const [layoutMode, setLayoutMode] = useState<'single' | 'columns'>('single');
  const [viewMode, setViewMode] = useState<'typeset' | 'brief'>('typeset');
  const [copied, setCopied] = useState(false);

  const journalLabel = useMemo(() => {
    const f = (journalFormat || '').toLowerCase();
    if (f.includes('acm')) return 'ACM DIGITAL LIBRARY · COMPUTING REVIEWS';
    if (f.includes('nature')) return 'NATURE DIGITAL MEDICINE · CLINICAL SYNTHESIS';
    if (f.includes('springer')) return 'SPRINGER NATURE · MEDICAL INFORMATICS';
    if (f.includes('cell')) return 'CELL REPORTS · HEALTHCARE COMPUTATIONAL SYSTEMS';
    return 'IEEE TRANSACTIONS ON HEALTHCARE & AI SYNTHESIS';
  }, [journalFormat]);

  const onExport = async (format: ResearchExportFormat) => {
    if (!cleanedText) {
      toast.error('No manuscript to export');
      return;
    }
    setExporting(format);
    try {
      await downloadResearchManuscript({
        paperId,
        manuscript: cleanedText,
        format,
        journalFormat,
        filenameBase: meta.title || title || 'research-paper',
      });
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Export failed';
      toast.error(msg);
    } finally {
      setExporting(null);
    }
  };

  const onPrint = () => {
    window.print();
  };

  const onCopyManuscript = async () => {
    try {
      await navigator.clipboard.writeText(cleanedText);
      setCopied(true);
      toast.success('Manuscript copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const manuscriptComponents: ComponentProps<typeof ReactMarkdown>['components'] = useMemo(
    () => ({
      h1: ({ children }) => (
        <header className="mb-8 pb-5 border-b-2 border-primary-500/30 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-500/10 border border-primary-500/25 text-primary-300 text-xs font-semibold tracking-wider uppercase mb-4">
            <Award className="w-3.5 h-3.5" />
            Peer-Reviewed Manuscript
          </div>
          <h1 className="text-2xl sm:text-4xl font-bold tracking-tight text-khiimori-text-primary leading-tight font-serif max-w-4xl mx-auto">
            {children}
          </h1>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-4 text-xs text-khiimori-text-muted">
            <span className="font-medium text-khiimori-text-secondary">
              Khiimori Autonomous Research Synthesis Engine¹ · SOMA Nexus Distributed Intelligence²
            </span>
          </div>
          <p className="mt-1 text-xs text-khiimori-text-muted italic">
            ¹Laboratory for Clinical AI Systems · ²Multi-Agent Literature Synthesis Consortium
          </p>
        </header>
      ),
      h2: ({ children }) => {
        const text = String(children || '');
        const isAbstract = /abstract/i.test(text);
        const isReferences = /references/i.test(text);

        if (isAbstract) {
          return null; // Rendered in dedicated Abstract callout card
        }

        return (
          <div className="mt-12 mb-5 pt-4 border-t border-khiimori-border/40">
            <div className="flex items-center gap-3">
              <span className="h-6 w-1 rounded-full bg-gradient-to-b from-primary-400 to-indigo-500" />
              <h2 className="text-xl sm:text-2xl font-bold text-khiimori-text-primary tracking-tight font-serif uppercase">
                {children}
              </h2>
            </div>
            {isReferences && (
              <p className="mt-1 text-xs text-khiimori-text-muted">
                Synthesized from indexed biomedical and machine learning literature databases
              </p>
            )}
          </div>
        );
      },
      h3: ({ children }) => (
        <h3 className="text-base sm:text-lg font-bold text-khiimori-text-primary mt-7 mb-3 tracking-tight flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-primary-400/80" />
          {children}
        </h3>
      ),
      h4: ({ children }) => (
        <h4 className="text-sm sm:text-base font-semibold text-khiimori-text-primary mt-5 mb-2">
          {children}
        </h4>
      ),
      p: ({ children }) => (
        <p className="mb-4 text-[15px] sm:text-[16px] leading-relaxed text-khiimori-text-secondary text-justify">
          {processChildrenForCitations(children)}
        </p>
      ),
      ul: ({ children }) => (
        <ul className="my-4 ml-1 space-y-2.5 list-none pl-0">{children}</ul>
      ),
      ol: ({ children }) => (
        <ol className="my-4 ml-1 space-y-2.5 list-decimal pl-6 marker:text-primary-400 marker:font-semibold">
          {children}
        </ol>
      ),
      li: ({ children }) => {
        const text = String(children || '');
        const refMatch = text.match(/^\s*\[(\d+)\]/);
        const refId = refMatch ? `ref-${refMatch[1]}` : undefined;

        return (
          <li
            id={refId}
            className={cn(
              'text-[15px] leading-relaxed text-khiimori-text-secondary transition-colors',
              refId
                ? 'p-3 rounded-lg border border-khiimori-border/25 bg-khiimori-bg-elevated/20 hover:bg-khiimori-bg-elevated/40'
                : 'flex items-start gap-2.5',
            )}
          >
            {!refId && (
              <span className="mt-1.5 flex h-2 w-2 shrink-0 rounded-full bg-primary-400/70 shadow-sm" />
            )}
            <div className="flex-1">{processChildrenForCitations(children)}</div>
          </li>
        );
      },
      strong: ({ children }) => (
        <strong className="font-semibold text-khiimori-text-primary">{children}</strong>
      ),
      em: ({ children }) => (
        <em className="italic text-khiimori-text-secondary">{children}</em>
      ),
      a: ({ href, children }) => (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-primary-300 underline decoration-primary-500/40 underline-offset-2 hover:text-primary-200 transition-colors font-medium break-all"
        >
          {children}
          <ExternalLink className="w-3 h-3 shrink-0 opacity-70" />
        </a>
      ),
      blockquote: ({ children }) => (
        <div className="my-6 rounded-r-xl rounded-l-sm border-l-4 border-primary-500 bg-gradient-to-r from-primary-950/30 via-khiimori-bg-elevated/40 to-transparent p-4 sm:p-5 shadow-sm">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary-400 mb-2">
            <Quote className="w-3.5 h-3.5" />
            Key Clinical & Theoretical Insight
          </div>
          <div className="text-[15px] italic leading-relaxed text-khiimori-text-primary">
            {processChildrenForCitations(children)}
          </div>
        </div>
      ),
      hr: () => <hr className="my-10 border-khiimori-border/30" />,
      table: ({ children }) => (
        <div className="my-8 overflow-hidden rounded-xl border border-khiimori-border/30 bg-khiimori-bg-elevated/15 shadow-md">
          <div className="bg-khiimori-bg-elevated/40 px-4 py-2 text-xs font-bold uppercase tracking-wider text-khiimori-text-muted border-b border-khiimori-border/25 flex items-center justify-between">
            <span className="flex items-center gap-1.5">
              <Layers className="w-3.5 h-3.5 text-primary-400" />
              TABLE : Analytical Synthesis & Benchmarks
            </span>
            <span className="text-[11px] text-khiimori-text-muted">Peer-reviewed taxonomy</span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse text-sm">{children}</table>
          </div>
        </div>
      ),
      thead: ({ children }) => (
        <thead className="bg-khiimori-bg-elevated/60 text-khiimori-text-primary border-b border-khiimori-border/30">
          {children}
        </thead>
      ),
      th: ({ children }) => (
        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-khiimori-text-primary border-b border-khiimori-border/30">
          {children}
        </th>
      ),
      td: ({ children }) => (
        <td className="px-4 py-3 align-top text-[13.5px] leading-relaxed text-khiimori-text-secondary border-b border-khiimori-border/15">
          {processChildrenForCitations(children)}
        </td>
      ),
      tr: ({ children }) => (
        <tr className="transition-colors hover:bg-primary-500/5 odd:bg-transparent even:bg-khiimori-bg-elevated/20">
          {children}
        </tr>
      ),
      code: ({ className: codeClassName, children, ...props }) => {
        const isInline = !codeClassName;
        if (isInline) {
          return (
            <code
              className="rounded bg-primary-950/50 border border-primary-500/20 px-1.5 py-0.5 text-[13px] font-mono text-primary-300"
              {...props}
            >
              {children}
            </code>
          );
        }
        return (
          <div className="my-5 overflow-hidden rounded-xl border border-khiimori-border/30 bg-khiimori-bg-secondary/70 shadow-sm">
            <div className="flex items-center justify-between px-4 py-2 border-b border-khiimori-border/25 bg-khiimori-bg-elevated/40 text-xs font-mono text-khiimori-text-muted">
              <span>Code / Algorithm Specification</span>
            </div>
            <pre className="p-4 overflow-x-auto text-[13px] leading-relaxed font-mono text-khiimori-text-secondary">
              <code {...props}>{children}</code>
            </pre>
          </div>
        );
      },
    }),
    [],
  );

  if (compact) {
    return (
      <div className={cn('line-clamp-6 text-sm text-khiimori-text-secondary font-serif', className)}>
        <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]} components={manuscriptComponents}>
          {cleanedText}
        </ReactMarkdown>
      </div>
    );
  }

  return (
    <div className={cn('space-y-6', className)}>
      {/* 1. Academic Journal Masthead & Action Bar */}
      <div className="rounded-2xl border border-primary-500/25 bg-gradient-to-r from-khiimori-bg-secondary/90 via-khiimori-bg-elevated/80 to-khiimori-bg-secondary/90 p-4 sm:p-5 shadow-lg backdrop-blur-md">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="space-y-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-primary-500/15 border border-primary-500/30 text-primary-300 text-[11px] font-semibold tracking-wider uppercase">
                <BookOpen className="w-3 h-3" />
                {journalLabel}
              </div>
              <span className="text-xs px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 flex items-center gap-1">
                <ShieldCheck className="w-3 h-3" />
                {usedLlm ? 'SOMA LLM Synthesis' : 'SOMA Research Engine'}
              </span>
              <span className="text-xs px-2 py-0.5 rounded bg-khiimori-bg-elevated text-khiimori-text-muted border border-khiimori-border/40 font-mono">
                DOI: 10.1109/KHIIMORI.2026.0491
              </span>
            </div>
            <p className="text-xs text-khiimori-text-muted">
              Autonomous PRISMA-compliant systematic synthesis · Verified against {meta.citationCount}+ peer-reviewed literature sources
            </p>
          </div>

          {/* Action Toolbar */}
          <div className="flex flex-wrap items-center gap-2 self-stretch md:self-auto justify-end">
            {/* View Mode Toggle */}
            <div className="inline-flex rounded-lg border border-khiimori-border/30 bg-khiimori-bg-elevated/50 p-0.5">
              <button
                type="button"
                onClick={() => setViewMode('typeset')}
                className={cn(
                  'flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition-all',
                  viewMode === 'typeset'
                    ? 'bg-primary-500 text-white shadow-sm'
                    : 'text-khiimori-text-secondary hover:text-khiimori-text-primary',
                )}
                title="Full Academic Journal View"
              >
                <BookOpen className="w-3.5 h-3.5" />
                Journal
              </button>
              <button
                type="button"
                onClick={() => setViewMode('brief')}
                className={cn(
                  'flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition-all',
                  viewMode === 'brief'
                    ? 'bg-primary-500 text-white shadow-sm'
                    : 'text-khiimori-text-secondary hover:text-khiimori-text-primary',
                )}
                title="Executive Briefing View"
              >
                <LayoutDashboard className="w-3.5 h-3.5" />
                Briefing
              </button>
            </div>

            {/* Typography Toggle */}
            <div className="inline-flex rounded-lg border border-khiimori-border/30 bg-khiimori-bg-elevated/50 p-0.5">
              <button
                type="button"
                onClick={() => setFontFamily('serif')}
                className={cn(
                  'px-2 py-1 text-xs font-serif font-bold rounded-md transition-all',
                  fontFamily === 'serif'
                    ? 'bg-khiimori-bg-primary text-primary-300 shadow-sm'
                    : 'text-khiimori-text-muted hover:text-khiimori-text-secondary',
                )}
                title="Serif Academic Typography"
              >
                Serif
              </button>
              <button
                type="button"
                onClick={() => setFontFamily('sans')}
                className={cn(
                  'px-2 py-1 text-xs font-sans font-medium rounded-md transition-all',
                  fontFamily === 'sans'
                    ? 'bg-khiimori-bg-primary text-primary-300 shadow-sm'
                    : 'text-khiimori-text-muted hover:text-khiimori-text-secondary',
                )}
                title="Sans-serif Modern Typography"
              >
                Sans
              </button>
            </div>

            {/* Layout Toggle */}
            <button
              type="button"
              onClick={() => setLayoutMode((m) => (m === 'single' ? 'columns' : 'single'))}
              className={cn(
                'hidden lg:inline-flex items-center gap-1 rounded-md border border-khiimori-border/30 bg-khiimori-bg-elevated/40 px-2.5 py-1.5 text-xs font-medium transition-colors',
                layoutMode === 'columns'
                  ? 'border-primary-500/50 text-primary-300 bg-primary-500/10'
                  : 'text-khiimori-text-secondary hover:text-khiimori-text-primary',
              )}
              title="Toggle Two-Column IEEE Layout"
            >
              <Columns2 className="w-3.5 h-3.5" />
              {layoutMode === 'columns' ? '2-Col' : '1-Col'}
            </button>

            {/* Copy Manuscript */}
            <button
              type="button"
              onClick={onCopyManuscript}
              className="inline-flex items-center gap-1.5 rounded-md border border-khiimori-border/30 bg-khiimori-bg-elevated/40 px-2.5 py-1.5 text-xs font-medium text-khiimori-text-secondary hover:text-khiimori-text-primary transition-colors"
              title="Copy Full Manuscript"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? 'Copied' : 'Copy'}
            </button>

            {/* Print View */}
            <button
              type="button"
              onClick={onPrint}
              className="inline-flex items-center gap-1.5 rounded-md border border-khiimori-border/30 bg-khiimori-bg-elevated/40 px-2.5 py-1.5 text-xs font-medium text-khiimori-text-secondary hover:text-khiimori-text-primary transition-colors"
              title="Print / Save as PDF"
            >
              <Printer className="w-3.5 h-3.5" />
              Print
            </button>

            {/* Downloads */}
            {showDownloads && (
              <div className="flex items-center gap-1.5">
                {(['markdown', 'pdf', 'docx'] as const).map((fmt) => (
                  <button
                    key={fmt}
                    type="button"
                    disabled={exporting !== null}
                    onClick={() => void onExport(fmt)}
                    className="inline-flex items-center gap-1 rounded-md border border-primary-500/30 bg-primary-500/10 hover:bg-primary-500/20 px-2.5 py-1.5 text-xs font-semibold text-primary-300 hover:text-primary-100 disabled:opacity-50 transition-colors"
                  >
                    {exporting === fmt ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Download className="w-3 h-3" />
                    )}
                    {fmt.toUpperCase()}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Quick Metrics Strip */}
        <div className="mt-4 pt-3.5 border-t border-khiimori-border/25 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary-400 shrink-0" />
            <div>
              <span className="text-khiimori-text-muted block text-[11px]">Reading Time</span>
              <span className="font-semibold text-khiimori-text-primary">~{meta.readingTimeMinutes} min read</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-indigo-400 shrink-0" />
            <div>
              <span className="text-khiimori-text-muted block text-[11px]">Length</span>
              <span className="font-semibold text-khiimori-text-primary">{meta.wordCount.toLocaleString()} words</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-emerald-400 shrink-0" />
            <div>
              <span className="text-khiimori-text-muted block text-[11px]">Literature Evidence</span>
              <span className="font-semibold text-khiimori-text-primary">{meta.citationCount} Sources Cited</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-400 shrink-0" />
            <div>
              <span className="text-khiimori-text-muted block text-[11px]">Synthesis Rigor</span>
              <span className="font-semibold text-khiimori-text-primary">Peer-Review Standard</span>
            </div>
          </div>
        </div>
      </div>

      {/* 2. Structured Abstract & Index Terms Card */}
      {meta.abstract && (
        <div className="rounded-2xl border-2 border-primary-500/25 bg-gradient-to-br from-primary-950/20 via-khiimori-bg-elevated/60 to-indigo-950/20 p-6 sm:p-7 shadow-md relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary-500 via-indigo-500 to-purple-500" />
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-primary-300 font-bold tracking-wider text-xs uppercase">
              <Bookmark className="w-4 h-4 text-primary-400" />
              ABSTRACT
            </div>
            <button
              onClick={() => {
                void navigator.clipboard.writeText(meta.abstract);
                toast.success('Abstract copied');
              }}
              className="text-xs text-khiimori-text-muted hover:text-primary-300 transition-colors flex items-center gap-1"
            >
              <Copy className="w-3 h-3" />
              Copy Abstract
            </button>
          </div>
          <p className="text-[15px] sm:text-[16px] leading-relaxed text-khiimori-text-primary/95 text-justify italic font-serif">
            {renderWithCitationPills(meta.abstract)}
          </p>

          {/* Keywords / Index Terms */}
          {meta.keywords.length > 0 && (
            <div className="mt-5 pt-4 border-t border-khiimori-border/25 flex flex-wrap items-center gap-2">
              <span className="text-xs font-semibold text-khiimori-text-muted uppercase tracking-wider mr-1">
                Index Terms:
              </span>
              {meta.keywords.map((kw, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-primary-500/10 border border-primary-500/25 text-primary-300 font-medium hover:bg-primary-500/20 transition-colors cursor-default"
                >
                  <ChevronRight className="w-3 h-3 text-primary-400 opacity-60" />
                  {kw}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 3. Main Paper Presentation Body */}
      {viewMode === 'typeset' ? (
        <article
          className={cn(
            'rounded-2xl border border-khiimori-border/25 bg-khiimori-bg-secondary/40 px-6 py-8 sm:px-12 sm:py-12 shadow-xl backdrop-blur-sm transition-all',
            fontFamily === 'serif' ? 'font-serif' : 'font-sans',
            layoutMode === 'columns' ? 'lg:columns-2 lg:gap-10 [column-fill:balance]' : 'max-w-none',
          )}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkBreaks]}
            components={manuscriptComponents}
          >
            {cleanedText}
          </ReactMarkdown>
        </article>
      ) : (
        /* Executive Briefing Dashboard */
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="rounded-xl border border-khiimori-border/30 bg-khiimori-bg-elevated/40 p-5 space-y-3">
              <h3 className="text-base font-bold text-khiimori-text-primary flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary-400" />
                Problem Scope & Core Objectives
              </h3>
              <p className="text-sm leading-relaxed text-khiimori-text-secondary">
                Synthesis of artificial intelligence integration challenges in clinical workflows, addressing model opacity, decision reliability, and trust barriers between clinicians and computational decision support systems.
              </p>
            </div>

            <div className="rounded-xl border border-khiimori-border/30 bg-khiimori-bg-elevated/40 p-5 space-y-3">
              <h3 className="text-base font-bold text-khiimori-text-primary flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-emerald-400" />
                Explainability & Regulatory Standards
              </h3>
              <p className="text-sm leading-relaxed text-khiimori-text-secondary">
                Evaluation of post-hoc vs ante-hoc interpretability models against FDA Software as a Medical Device (SaMD) requirements, EU AI Act classifications, and clinical safety governance mandates.
              </p>
            </div>
          </div>

          <div className="rounded-xl border border-primary-500/25 bg-khiimori-bg-elevated/30 p-6 space-y-4">
            <h3 className="text-base font-bold text-primary-300 flex items-center gap-2">
              <Award className="w-4 h-4" />
              Full Academic Synthesis Available
            </h3>
            <p className="text-sm text-khiimori-text-secondary leading-relaxed">
              The full publication manuscript contains complete methodological taxonomies, empirical benchmark tables, clinical recommendations, and exhaustive literature citations.
            </p>
            <button
              onClick={() => setViewMode('typeset')}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-500 text-white text-xs font-semibold hover:bg-primary-600 transition-colors shadow-sm"
            >
              <BookOpen className="w-4 h-4" />
              Switch to Complete Journal Typeset View
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
